/**
 * diaryInstantFeedback — 日記を書いた直後に出す「AI気づき」1行。
 *
 * 設計上の位置づけ（diary-analysis-overhaul.md Phase 2 残務）:
 *   - 「事後集計じゃなく、書いた瞬間に返ってくる小さな気づき」を提供する。
 *   - 表現ルール（数字を出さない）に従う: 指数スコアは書かない。許容は「3週間ぶり」
 *     「3日続いてる」「あなたにしては〜」のような時系列・本人ベースライン比のみ。
 *   - 助言（「だから〜すべき」）は書かない。発見した事実だけ。
 *   - 該当する気づきが無ければ空文字を返す（無理に喋らない）。
 *
 * LLM 呼ばずに直近の emotion_analysis から決定論的に組み立てる。即時返答・低コスト。
 */

import { supabase } from '@/lib/supabase'

const PLUTCHIK_KEYS = ['joy', 'trust', 'fear', 'surprise', 'sadness', 'disgust', 'anger', 'anticipation'] as const
type PlutchikKey = typeof PLUTCHIK_KEYS[number]

const LABEL_JA: Record<PlutchikKey, string> = {
  joy: '喜び',
  trust: '信頼',
  fear: '不安',
  surprise: '驚き',
  sadness: '悲しみ',
  disgust: '嫌悪',
  anger: '怒り',
  anticipation: '期待',
}

/** 「強く出てる」と判定する強度の閾値（plutchik は 0–100）。 */
const STRONG = 30
/** WBI が「いつもと違う」と判定する平均との差（WBI は 0–10）。 */
const WBI_LOW_DIFF = 1.2
const WBI_HIGH_DIFF = 1.5

interface RecentRow {
  diary_entry_id: number
  joy: number
  trust: number
  fear: number
  surprise: number
  sadness: number
  disgust: number
  anger: number
  anticipation: number
  wbi_score: number
  created_at: string
}

export interface FreshEmotion {
  plutchik: Record<PlutchikKey, number>
  wbi: number
}

/**
 * 直近30件と比較して1行の気づきを返す。何も特筆なければ '' を返す。
 *
 * 判定優先順位:
 *   1. dominant な感情（>= STRONG）が直近 14日以上ぶり → 「{感情}が{N}週間ぶりに出てる」
 *   2. dominant な感情が直近 3日以上連続 → 「{感情}が{N}日続いてる」
 *   3. dominant な感情が直近 N件にゼロ → 「{感情}の言葉が珍しく出てる日」
 *   4. WBI が直近平均より明らかに低い → 「あなたにしては気分が低い日」
 *   5. WBI が直近平均より明らかに高い → 「あなたにしては気分が高い日」
 */
export async function instantFeedback(today: FreshEmotion): Promise<string> {
  const { data } = await supabase
    .from('emotion_analysis')
    .select('diary_entry_id,joy,trust,fear,surprise,sadness,disgust,anger,anticipation,wbi_score,created_at')
    .order('created_at', { ascending: false })
    .limit(30)
  const recent = (data as RecentRow[] | null) ?? []
  if (recent.length < 3) return ''
  // 直近は today を含む可能性が高い（保存直後）。created_at 降順の先頭が同じ日記なら除外。
  const prior = recent.slice(recent[0]?.diary_entry_id ? 1 : 0)
  if (prior.length < 3) return ''

  // dominant 感情
  const ranked = PLUTCHIK_KEYS
    .map((k): [PlutchikKey, number] => [k, today.plutchik[k] ?? 0])
    .sort((a, b) => b[1] - a[1])
  const [domKey, domVal] = ranked[0]

  if (domVal >= STRONG) {
    const prevHits = prior.filter((r) => (r[domKey] ?? 0) >= STRONG)
    if (prevHits.length === 0) {
      return `${LABEL_JA[domKey]}の言葉が珍しく出てる日`
    }
    const lastHit = new Date(prevHits[0].created_at).getTime()
    const days = Math.floor((Date.now() - lastHit) / 86400000)
    if (days >= 14) {
      const weeks = Math.round(days / 7)
      return `${LABEL_JA[domKey]}が${weeks}週間ぶりに出てる`
    }
    // 連続日数（直近側から数える）
    let streak = 1
    for (const r of prior) {
      if ((r[domKey] ?? 0) >= STRONG) streak += 1
      else break
    }
    if (streak >= 3) return `${LABEL_JA[domKey]}が${streak}日続いてる`
  }

  // WBI ベースの比較（dominant が弱い・もしくは何も該当しなかった時のフォールバック）
  if (prior.length >= 5 && Number.isFinite(today.wbi)) {
    const sample = prior.slice(0, 14)
    const avg = sample.reduce((s, r) => s + (r.wbi_score ?? 0), 0) / sample.length
    const diff = today.wbi - avg
    if (diff <= -WBI_LOW_DIFF) return 'あなたにしては気分が低い日'
    if (diff >= WBI_HIGH_DIFF) return 'あなたにしては気分が高い日'
  }

  return ''
}
